import React, { useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Select from 'react-select'
import Swal from 'sweetalert2'

import {
  fetchCustomers,
  fetchClientCommissions,
  saveClientCommissions,
} from './apis/requestsApi'
import { ClientCommissionItem } from './core/CommissionClient.model'
import { parseInput, validateValue, fmt } from '../commissionDefault/core/CommissionDefault.utils'

interface CustomerOption {
  value: number
  label: string
}

const DEFAULT_PARTNERS = ['Passport', 'Conekta', 'Enefevo']

type RowUI = {
  customerId: number
  techPartner: string
  comissionBaseTech: string
  comissionBaseCommercial: string
  isDefault: boolean
  isPercentageTech: boolean // 🟢 nuevo
  isPercentageCommercial: boolean // 🟢 nuevo
}

export const ClientCommission: React.FC = () => {
  const intl = useIntl()

  const [customers, setCustomers] = useState<CustomerOption[]>([])
  const [customerId, setCustomerId] = useState<number | null>(null)
  const [data, setData] = useState<RowUI[]>([])
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState<boolean>(true)
  const [saving, setSaving] = useState<boolean>(false)

  const activeBtnStyle: React.CSSProperties = {
    backgroundColor: 'var(--kt-primary)',
    color: '#fff',
    borderColor: 'var(--kt-primary)',
  }
  const inactiveBtnStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    color: 'var(--kt-primary)',
    borderColor: 'var(--kt-primary)',
  }

  // 1️⃣ Cargar clientes
  useEffect(() => {
    (async () => {
      try {
        const raw = await fetchCustomers()
        setCustomers(raw.map((c: any) => ({ value: c.id, label: c.name })))
      } catch {
        Swal.fire('Error', 'No se pudo cargar la lista de clientes', 'error')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  // 2️⃣ Cargar comisiones por cliente
  useEffect(() => {
    if (customerId == null) return
    ;(async () => {
      setLoading(true)
      try {
        const id = customerId!
        const list: ClientCommissionItem[] = await fetchClientCommissions(id)

        const formatted: RowUI[] = (list && list.length
          ? list
          : DEFAULT_PARTNERS.map(tp => ({
              customerId: id,
              techPartner: tp,
              comissionBaseTech: '',
              comissionBaseCommercial: '',
              isDefault: true,
              isPercentageTech: true, // 🟢 nuevo default
              isPercentageCommercial: true, // 🟢 nuevo default
            }))
        ).map((r: any) => ({
          customerId: id,
          techPartner: r.techPartner,
          comissionBaseTech: fmt(r.comissionBaseTech ?? r.pctBaseTech ?? ''),
          comissionBaseCommercial: fmt(r.comissionBaseCommercial ?? r.pctBaseCommercial ?? ''),
          isDefault: !!r.isDefault,
          isPercentageTech: typeof r.isPercentageTech === 'boolean' ? r.isPercentageTech : true,
          isPercentageCommercial:
            typeof r.isPercentageCommercial === 'boolean' ? r.isPercentageCommercial : true,
        }))

        setData(formatted)
        setErrors({})
      } catch {
        Swal.fire('Error', 'No se pudo cargar la configuración', 'error')
      } finally {
        setLoading(false)
      }
    })()
  }, [customerId])

  // 3️⃣ Validación por celda (usa flags por fila)
  const revalidateCell = (
    idx: number,
    field: 'comissionBaseTech' | 'comissionBaseCommercial',
    raw: string
  ) => {
    const row = data[idx]
    const isPct = field === 'comissionBaseTech' ? row.isPercentageTech : row.isPercentageCommercial
    const numeric = parseInput(raw)
    const key = `${idx}-${field}`
    if (!validateValue(numeric, isPct) && raw !== '') {
      setErrors(e => ({
        ...e,
        [key]: isPct ? '0–100, hasta 4 decimales' : '≥ 0, hasta 4 decimales',
      }))
    } else {
      setErrors(e => {
        const { [key]: _, ...rest } = e
        return rest
      })
    }
  }

  // 4️⃣ Cambios en inputs
  const onChange = (
    idx: number,
    field: 'comissionBaseTech' | 'comissionBaseCommercial',
    raw: string
  ) => {
    setData(prev => {
      const upd = [...prev]
      upd[idx]![field] = raw
      upd[idx]!.isDefault = false
      return upd
    })
    revalidateCell(idx, field, raw)
  }

  // 🟢 nuevo: toggle individual % / $
  const toggleType = (idx: number, field: 'isPercentageTech' | 'isPercentageCommercial', value: boolean) => {
    setData(prev => {
      const copy = [...prev]
      copy[idx]![field] = value
      return copy
    })
  }

  // 5️⃣ Validación general
  const canSave = useMemo(
    () =>
      customerId !== null &&
      Object.keys(errors).length === 0 &&
      !saving &&
      data.every(
        r =>
          Number.isFinite(parseInput(r.comissionBaseTech)) &&
          Number.isFinite(parseInput(r.comissionBaseCommercial))
      ),
    [customerId, errors, saving, data]
  )

  // 6️⃣ Guardar
  const handleSave = async () => {
    if (!canSave) return
    setSaving(true)
    Swal.fire({ title: 'Guardando…', didOpen: () => Swal.showLoading(), allowOutsideClick: false })
    try {
      const payload: ClientCommissionItem[] = data.map(row => ({
        customerId: row.customerId,
        techPartner: row.techPartner,
        comissionBaseTech: parseInput(row.comissionBaseTech),
        isPercentageTech: row.isPercentageTech, // 🟢 individual
        comissionBaseCommercial: parseInput(row.comissionBaseCommercial),
        isPercentageCommercial: row.isPercentageCommercial, // 🟢 individual
        isDefault: row.isDefault,
      }))
      await saveClientCommissions(customerId!, payload)
      Swal.close()
      Swal.fire('¡Listo!', 'Comisión personalizada guardada', 'success')
    } catch {
      Swal.close()
      Swal.fire('Error al guardar', 'Intenta de nuevo', 'error')
    } finally {
      setSaving(false)
    }
  }

  // 7️⃣ Render
  return (
    <div className='card mb-5 mb-xl-10 position-relative'>
      {loading && (
        <div className='position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white opacity-75'>
          <span className='spinner-border text-primary mb-3' role='status' />
          <strong>Cargando…</strong>
        </div>
      )}

      <div className='card-body py-3'>
        <div className='d-flex flex-wrap align-items-center justify-content-between mb-5'>
          <h3 className='mb-3 mb-md-0'>
            {intl.formatMessage({
              id: 'CLIENT_COMMISSION.TITLE',
              defaultMessage: 'Comisión Personalizada del Cliente',
            })}
          </h3>
          <button className='btn btn-primary rounded-pill' onClick={handleSave} disabled={!canSave}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>

        {/* Filtro cliente */}
        <div className='row flex-wrap mb-5'>
          <div className='col-lg-4 col-md-6 col-sm-12'>
            <label className='form-label'>Cliente</label>
            <Select<CustomerOption>
              options={customers}
              value={customers.find(c => c.value === customerId) ?? null}
              onChange={opt => setCustomerId(opt?.value ?? null)}
              isDisabled={loading || saving}
              placeholder='Selecciona un cliente…'
              classNamePrefix='react-select'
            />
          </div>
        </div>

        {/* Tabla */}
        <div className='table-responsive'>
          <table className='table table-striped align-middle'>
            <thead>
              <tr className='fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200'>
                <th>Socio Tecnológico</th>
                <th>Comisión socio tecnológico</th>
                <th>Comisión socio comercial</th>
              </tr>
            </thead>

            <tbody>
              {data.map((row, idx) => (
                <tr key={row.techPartner}>
                  <td>{row.techPartner}</td>

                  {/* Tech */}
                  <td>
                    <div className='d-flex align-items-center gap-2'>
                      <div className='btn-group me-2'>
                        <button
                          type='button'
                          className='btn btn-sm'
                          style={row.isPercentageTech ? activeBtnStyle : inactiveBtnStyle}
                          onClick={() => toggleType(idx, 'isPercentageTech', true)}
                        >
                          %
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm'
                          style={!row.isPercentageTech ? activeBtnStyle : inactiveBtnStyle}
                          onClick={() => toggleType(idx, 'isPercentageTech', false)}
                        >
                          $
                        </button>
                      </div>

                      {!row.isPercentageTech && <span className='me-1'>$</span>}
                      <input
                        type='text'
                        disabled={saving || loading}
                        placeholder={row.isPercentageTech ? '0 – 100' : '≥ 0.0000'}
                        className={`form-control form-control-solid ${errors[`${idx}-comissionBaseTech`] ? 'is-invalid' : ''}`}
                        value={row.comissionBaseTech}
                        onChange={e => onChange(idx, 'comissionBaseTech', e.target.value)}
                      />
                      {row.isPercentageTech && <span className='ms-1'>%</span>}
                    </div>
                  </td>

                  {/* Commercial */}
                  <td>
                    <div className='d-flex align-items-center gap-2'>
                      <div className='btn-group me-2'>
                        <button
                          type='button'
                          className='btn btn-sm'
                          style={row.isPercentageCommercial ? activeBtnStyle : inactiveBtnStyle}
                          onClick={() => toggleType(idx, 'isPercentageCommercial', true)}
                        >
                          %
                        </button>
                        <button
                          type='button'
                          className='btn btn-sm'
                          style={!row.isPercentageCommercial ? activeBtnStyle : inactiveBtnStyle}
                          onClick={() => toggleType(idx, 'isPercentageCommercial', false)}
                        >
                          $
                        </button>
                      </div>

                      {!row.isPercentageCommercial && <span className='me-1'>$</span>}
                      <input
                        type='text'
                        disabled={saving || loading}
                        placeholder={row.isPercentageCommercial ? '0 – 100' : '≥ 0.0000'}
                        className={`form-control form-control-solid ${errors[`${idx}-comissionBaseCommercial`] ? 'is-invalid' : ''}`}
                        value={row.comissionBaseCommercial}
                        onChange={e => onChange(idx, 'comissionBaseCommercial', e.target.value)}
                      />
                      {row.isPercentageCommercial && <span className='ms-1'>%</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
